import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import UseGetDataSearch from "../../../../Components/Hooks/useFetchDataBySearch";
import UseDropDown from "../../../../Components/Hooks/useDropDown";
import UseFetchCurrency from "../../../../Components/Hooks/useFetchCurrencyData";
import SearchBar from "../../../../Components/Utils/SearchBar";
import {
  CancelButton,
  CreateButton,
  FilterButton,
  SubmitButton,
} from "../../../../Components/Utils/Buttons";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import ViewToggle from "../../../../Components/Utils/ViewToggle";
import FormikSelectTwo from "../../../../Components/Utils/Select";
import BanksTable from "./BanksTable";
import { GetAccess, GetRole } from "../../../../Components/Utils/roles";

function BanksIndex() {
  const location = useLocation();
  const currentPage = location.state?.currentPage;
  const searchQuery = location.state?.searchQuery;

  const [page, setPage] = useState(currentPage || 1);
  const [pageCount, setPageCount] = useState(
    Number(localStorage.getItem("perPageView")) || 20
  );
  const [viewMode, setViewMode] = useState(
    localStorage.getItem("banksViewMode") || "table"
  );
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const [filter, setFilter] = useState(false);
  const [showFilterDialog, setShowFilterDialog] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const effectiveViewMode = isMobile ? "card" : (viewMode === "list" ? "table" : viewMode);

  // Dropdown options
  const { value: currencyOptions } = UseFetchCurrency("/currency/drop-down");
  const { data: userOptions } = UseDropDown("/user/drop-down");

  const [filterProps] = useState({
    currencyId: "",
    createdBy: "",
  });

  const [params, setParams] = useState({
    page: page,
    per_page: pageCount,
    search: searchQuery || "",
    currencyId: "",
    createdBy: "",
  });

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      page: page,
      per_page: pageCount,
    }));
  }, [page, pageCount]);

  const clearFilter = () => {
    setParams((prev) => ({
      ...prev,
      page: 1,
      currencyId: "",
      createdBy: "",
    }));
    formik.resetForm();
    setFilter(false);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: filterProps,
    onSubmit: async (values) => {
      setParams((prev) => ({
        ...prev,
        page: 1,
        currencyId: values.currencyId || "",
        createdBy: values.createdBy || "",
      }));
      setPage(1);
      setShowFilterDialog(false);
      const isAnyFilterActive = Boolean(values.currencyId || values.createdBy);
      setFilter(isAnyFilterActive);
    },
  });

  const { data, totalCount, getData, dataLoading } = UseGetDataSearch(
    "/banks/list",
    params
  );

  const canCreate =
    GetAccess("bank_details_create") || GetAccess("banks_create") || GetRole();

  return (
    <div className="w-full h-full px-2 sm:px-4 py-2">
      {/* Top Header Bar */}
      <div className="w-full flex flex-col md:flex-row justify-between items-stretch md:items-center gap-2 md:gap-3 mb-3">
        <div className="w-full md:w-auto flex justify-between items-center gap-2">
          <Breadcrumb
            items={[
              { name: "Payment", link: "/payments/banks" },
              { name: "Bank Details" },
            ]}
          />
          <div className="flex md:hidden items-center gap-2">
            <FilterButton handleClick={() => setShowFilterDialog(true)} />
            {filter && (
              <FilterButton handleClick={() => clearFilter()} label="Clear" />
            )}
            {canCreate && (
              <Link to="/payments/banks/create">
                <CreateButton label="Add Bank" />
              </Link>
            )}
          </div>
        </div>

        <div className="w-full md:w-auto flex flex-wrap md:flex-nowrap items-center gap-2">
          <div className="w-full md:w-64">
            <SearchBar
              handleChange={(e) => {
                setParams({ ...params, search: e.target.value, page: 1 });
                setPage(1);
              }}
              value={params.search}
              placeholder="Search banks, accounts..."
            />
          </div>

          <div className="hidden md:flex items-center gap-2">
            <FilterButton handleClick={() => setShowFilterDialog(true)} />
            {filter && (
              <FilterButton handleClick={() => clearFilter()} label="Clear" />
            )}
          </div>

          <ViewToggle
            viewMode={effectiveViewMode}
            onViewChange={(mode) => setViewMode(mode)}
            storageKey="banksViewMode"
          />

          <div className="hidden md:flex items-center gap-2">
            {canCreate && (
              <Link to="/payments/banks/create">
                <CreateButton label="Add Bank" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Table / Card List */}
      <BanksTable
        data={data}
        total={totalCount}
        pageIndex={page}
        setPageIndex={setPage}
        dataCount={pageCount}
        setDataCount={setPageCount}
        searchQuery={params?.search !== "" ? params?.search : ""}
        fetcher={getData}
        dataLoading={dataLoading}
        viewMode={effectiveViewMode}
      />

      {/* Filter Modal Dialog */}
      {showFilterDialog && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-xs z-[9999] flex justify-center items-center p-3 sm:p-4">
          <div className="animate-scale-in w-[92vw] sm:w-[500px] md:w-[560px] max-w-full max-h-[85vh] bg-white p-5 sm:p-6 rounded-2xl shadow-2xl overflow-y-auto flex flex-col">
            <div className="flex justify-between items-center pb-3 border-b border-gray-100 mb-4">
              <h2 className="text-lg font-bold text-gray-800">
                Filter Bank Details
              </h2>
              <button
                onClick={() => setShowFilterDialog(false)}
                type="button"
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={formik.handleSubmit} className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row flex-wrap -mx-2">
                {/* Currency */}
                <FormikSelectTwo
                  label="Currency"
                  placeholder="Select Currency"
                  Options={currencyOptions}
                  formik={formik}
                  name="currencyId"
                  width="w-full sm:w-1/2"
                  px="2"
                  isClear={true}
                />

                {/* Created By */}
                <FormikSelectTwo
                  label="Created By"
                  placeholder="Select User"
                  Options={userOptions}
                  formik={formik}
                  name="createdBy"
                  width="w-full sm:w-1/2"
                  px="2"
                  isClear={true}
                />
              </div>

              <div className="flex justify-end gap-2.5 pt-4 mt-2 border-t border-gray-100">
                <CancelButton handleClick={() => setShowFilterDialog(false)} />
                <SubmitButton name="Apply" />
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default BanksIndex;
